import { useState, useCallback, useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";

const MODAL_SIZE = 1000;
const IMG_SIZE = "h-72 w-72";
const BTN_SECONDARY =
  "px-4 py-2 text-sm font-medium rounded border border-gray-300 text-[#222222] hover:bg-gray-100 transition-colors";
const BTN_PRIMARY =
  "px-4 py-2 text-sm font-medium rounded border border-[#2AB9B8] text-[#2AB9B8] hover:bg-[#2AB9B8] hover:text-white transition-colors";
const BTN_FOOTER_SEC =
  "px-5 py-2.5 text-sm font-medium rounded border border-gray-300 text-[#222222] hover:bg-gray-100 transition-colors";
const BTN_FOOTER_PRI =
  "px-5 py-2.5 text-sm font-medium rounded text-white bg-[#2AB9B8] hover:bg-[#25a5a4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

const PDF_MODAL_STYLE = `
.pdf-image-selection-modal.modal { align-items: center; justify-content: center; }
.pdf-image-selection-modal .modal-dialog {
  position: fixed !important; top: 50% !important; left: 50% !important;
  transform: translate(-50%, -50%) !important; margin: 0 !important;
  width: min(${MODAL_SIZE}px, 90vw); max-width: min(${MODAL_SIZE}px, 90vw);
}
.pdf-image-selection-modal .modal-content {
  max-height: 85vh; display: flex; flex-direction: column; overflow: hidden;
}`;

const PdfImageSelectionModal = ({
  isOpen,
  onClose,
  allImages,
  defaultSelectedUrls,
  azureSASTokenToUrl,
  onConfirm,
  t,
  title,
  description,
}) => {
  const [selectedUrls, setSelectedUrls] = useState(() => new Set());
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-pdf-image-selection-modal", "true");
    style.textContent = PDF_MODAL_STYLE;
    document.head.appendChild(style);
    return () =>
      document.querySelector("[data-pdf-image-selection-modal]")?.remove();
  }, []);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current && defaultSelectedUrls)
      setSelectedUrls(new Set(defaultSelectedUrls));
    wasOpenRef.current = isOpen;
  }, [isOpen, defaultSelectedUrls]);

  const toggleImage = useCallback((url) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  }, []);

  const selectAll = useCallback(
    () => setSelectedUrls(new Set(allImages.map(({ item }) => item.image_url))),
    [allImages],
  );
  const selectNone = useCallback(() => setSelectedUrls(new Set()), []);
  const resetToDefault = useCallback(
    () => setSelectedUrls(new Set(defaultSelectedUrls || [])),
    [defaultSelectedUrls],
  );

  const handleConfirm = useCallback(() => {
    // Do not call onClose here: the parent closes the modal after PDF
    // generation and must keep off-screen charts mounted for html2canvas.
    onConfirm(selectedUrls);
  }, [onConfirm, selectedUrls]);

  if (!isOpen) return null;

  const selectedCount = selectedUrls.size;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      contentClassName="rounded"
      modalClassName="pdf-image-selection-modal"
    >
      <ModalHeader className="flex justify-between items-center border-0 px-6 pt-3 pb-2 border-b border-gray-200">
        <span className="text-[#222222] font-bold text-2xl">
          {title || t("pdfImageSelectionTitle")}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-[#222222] hover:text-[#5e5e5e] transition-colors bg-transparent border-0 p-0 cursor-pointer"
          aria-label={t("Close")}
        >
          <i className="fas fa-times text-lg" />
        </button>
      </ModalHeader>
      <ModalBody className="px-6 pt-2 pb-3 overflow-y-auto flex-1 min-h-0">
        <p className="text-[#222222] text-sm mb-4">
          {description || t("pdfImageSelectionDescription")}
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            className={BTN_PRIMARY}
            onClick={resetToDefault}
          >
            {t("pdfImageSelectionResetDefault")}
          </button>
          <button type="button" className={BTN_SECONDARY} onClick={selectAll}>
            {t("pdfImageSelectionSelectAll")}
          </button>
          <button type="button" className={BTN_SECONDARY} onClick={selectNone}>
            {t("pdfImageSelectionSelectNone")}
          </button>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2 min-h-[320px] border border-gray-200 rounded p-4 bg-gray-50">
          {allImages.map(({ item }) => {
            const url = item.image_url;
            const src = azureSASTokenToUrl ? azureSASTokenToUrl(url) : url;
            const isChecked = selectedUrls.has(url);
            return (
              <label
                key={url}
                className="flex-shrink-0 flex items-center gap-2 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleImage(url)}
                  className="w-5 h-5 flex-shrink-0 rounded border-gray-300 text-[#2AB9B8] focus:ring-[#2AB9B8]"
                />
                <div className="flex flex-col items-center">
                  <img
                    src={src}
                    alt=""
                    className={`${IMG_SIZE} object-cover rounded border-2 border-transparent group-hover:border-[#2AB9B8]`}
                    style={{ borderColor: isChecked ? "#2AB9B8" : undefined }}
                  />
                  <span className="text-xs text-[#5e5e5e] mt-1 truncate max-w-[288px]">
                    {item._time ? new Date(item._time).toLocaleString() : ""}
                  </span>
                </div>
              </label>
            );
          })}
        </div>
        <p className="text-sm text-[#222222] mt-2">
          {t("pdfImageSelectionCount", { count: selectedCount })}
        </p>
      </ModalBody>
      <ModalFooter className="flex justify-end gap-3 px-6 pb-5 pt-2 border-0 border-t border-gray-200">
        <button type="button" className={BTN_FOOTER_SEC} onClick={onClose}>
          {t("Cancel")}
        </button>
        <button
          type="button"
          className={BTN_FOOTER_PRI}
          onClick={handleConfirm}
          disabled={selectedCount === 0}
        >
          {t("pdfImageSelectionCreatePdf")}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default PdfImageSelectionModal;
